import type { AttributeDefinitionFragment } from '@generated/graphql';
import { sortByAnotherArray, sortByParam } from '@libs/comparer.helper';
import { CategoryHelper } from '@libs/category.helper';
import {
  GEARS_VARIANT_AXIS_ATTRIBUTES_ORDER,
  RC_VARIANT_AXIS_ATTRIBUTES_ORDER,
  ECHAIN_VARIANT_AXIS_ATTRIBUTES_ORDER,
  BALL_BEARINGS_VARIANT_AXIS_ATTRIBUTES_ORDER,
  TOOTHED_SLEWING_RINGS,
  SPHERICAL_BALLS,
  PRIMARY_CATEGORY,
  IGUBAL_DOUBLE_JOINTS_ORDER,
  IGUBAL_COUPLING_JOINTS_ORDER,
  DRYGEAR_ACCESSORIES_ORDER,
  DRYGEAR_WORM_GEAR_JOINTS_ASYMMETRICAL_ORDER,
  DRYGEAR_WORM_GEAR_ACCESSORIES_ORDER,
  BUS_CABLE_VARIANT_AXIS_ATTRIBUTES_ORDER,
  BALL_TRANSFER_UNIT_VARIANT_AXIS_ATTRIBUTES_ORDER,
  CONVEYOR_ROLLERS_VARIANT_AXIS_ATTRIBUTES_ORDER,
  FLANGE_BALL_BEARINGS_UNIT_VARIANT_AXIS_ATTRIBUTES_ORDER,
  MOTOR_GEAR_BOXES_ORDER,
} from '@libs/constants';
import type { VariantAxis, VariantAxisItem } from '@hooks/states/variantSelectionSlice';
import type { MinimalProductWithVariantAxesAttributes, ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { ProductAttributeValueAmountUnit, ProductAttribute } from './attribute.helper';
import { getAttributeValue } from './attribute.helper';

// this function recursively checks all preceding variant axis values for a product and if it matches all of them with a given selected other product
// if all variant axes match, it returns true, otherwise false
const doesProductMatchAllHigherLevelAxes = (
  axisCount: number,
  currentProduct: Pick<ProductFragmentEnhanced, 'attributes' | 'id'>,
  axesAttributesDefinitions: AttributeDefinitionFragment[],
  selectedProduct: ProductFragmentEnhanced
): boolean => {
  if (axisCount < 0) {
    return true;
  }

  const higherLevelAxisValueOfProduct = getAttributeValue<ProductAttributeValueAmountUnit>(
    currentProduct.attributes?.[
      axesAttributesDefinitions[axisCount].attribute_id
    ] as ProductAttribute<ProductAttributeValueAmountUnit>
  );
  const higherLevelAxisValueOfSelectedProduct = getAttributeValue<ProductAttributeValueAmountUnit>(
    selectedProduct.attributes?.[
      axesAttributesDefinitions[axisCount].attribute_id
    ] as ProductAttribute<ProductAttributeValueAmountUnit>
  );
  if (higherLevelAxisValueOfProduct.amount && higherLevelAxisValueOfSelectedProduct.amount) {
    if (higherLevelAxisValueOfProduct.amount !== higherLevelAxisValueOfSelectedProduct.amount) {
      return false;
    }
  } else if (higherLevelAxisValueOfProduct !== higherLevelAxisValueOfSelectedProduct) {
    return false;
  }

  return doesProductMatchAllHigherLevelAxes(axisCount - 1, currentProduct, axesAttributesDefinitions, selectedProduct);
};

export const generateAxisItems = (
  axisCount: number,
  products: Pick<ProductFragmentEnhanced, 'attributes' | 'id'>[],
  axesAttributesDefinitions: AttributeDefinitionFragment[],
  axisAttributeDefinition: AttributeDefinitionFragment,
  selectedProduct?: ProductFragmentEnhanced
): VariantAxisItem[] => {
  let items: VariantAxisItem[] = [];
  // create items for the current variant axis
  for (const product of products) {
    if (!product.id) {
      continue;
    }

    // in lower level axes we only want to show options of products that match the values of the higher level axes
    if (axisCount > 0 && selectedProduct) {
      if (!doesProductMatchAllHigherLevelAxes(axisCount - 1, product, axesAttributesDefinitions, selectedProduct)) {
        continue;
      }
    }

    // When the attributeDefinition contains the value in its attribute_values that means we have a custom translation that
    // shall be used instead of the actual value to be shown in the dropdown. If it doesn't match an entry in the attribute_values
    // we have a plain value without translation, so it can be used right away.
    const attributeValue = getAttributeValue<string>(
      product.attributes?.[axisAttributeDefinition.attribute_id] as ProductAttribute<string>
    );

    const attributeValueFromDefinition = axisAttributeDefinition.attribute_values?.find(
      attributeValueDefinition => attributeValueDefinition.value_key === attributeValue
    );

    const translatedAttributeValue = attributeValueFromDefinition?.label || attributeValue;

    if (!translatedAttributeValue || items.includes(translatedAttributeValue)) {
      continue;
    }

    items.push(translatedAttributeValue);
  }

  // we need to sort non-simple-select values in the frontend because we can't let do it hasura for us.
  // in those cases akeneo does not provide the sort-order prop for the axes values
  // TODO: perhaps this has to be modified when we also support IMPERIAL values
  if (axisAttributeDefinition.typename === 'METRIC') {
    items = items.sort(sortByParam<VariantAxisItem>(item => (typeof item === 'object' ? item.amount : item)));
  }

  return items;
};

/**
 * generates the variant axes data structure based on the given variants and attributes
 */
export const generateVariantAxes = (
  products: Array<Pick<ProductFragmentEnhanced, 'attributes' | 'id'>>,
  attributes: string[],
  attributeDefinitions: AttributeDefinitionFragment[],
  readonly: boolean,
  category: string,
  locale: string,
  selectedProduct?: ProductFragmentEnhanced
): VariantAxis[] => {
  // conserve the initial order of the attributes
  let attributeDefinitionsSorted = attributeDefinitions;

  // overwrite order of axis for certain categories
  if (CategoryHelper.isBusCable(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, BUS_CABLE_VARIANT_AXIS_ATTRIBUTES_ORDER)
    );
  }
  if (CategoryHelper.isReadycable(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, RC_VARIANT_AXIS_ATTRIBUTES_ORDER)
    );
  }
  if (CategoryHelper.isGear(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, GEARS_VARIANT_AXIS_ATTRIBUTES_ORDER)
    );
  }

  if (CategoryHelper.isEchain(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, ECHAIN_VARIANT_AXIS_ATTRIBUTES_ORDER)
    );
  }

  if (CategoryHelper.isBallBearings(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, BALL_BEARINGS_VARIANT_AXIS_ATTRIBUTES_ORDER)
    );
  }

  if (CategoryHelper.isFlangeBallBearings(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(
        a.attribute_id,
        b.attribute_id,
        FLANGE_BALL_BEARINGS_UNIT_VARIANT_AXIS_ATTRIBUTES_ORDER
      )
    );
  }

  if (CategoryHelper.isBallTransferUnit(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, BALL_TRANSFER_UNIT_VARIANT_AXIS_ATTRIBUTES_ORDER)
    );
  }

  if (CategoryHelper.isBallConveyorRollers(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, CONVEYOR_ROLLERS_VARIANT_AXIS_ATTRIBUTES_ORDER)
    );
  }

  if (CategoryHelper.isRadialBallBearings(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, BALL_BEARINGS_VARIANT_AXIS_ATTRIBUTES_ORDER)
    );
  }

  if (CategoryHelper.isToothedSlewingRings(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, TOOTHED_SLEWING_RINGS)
    );
  }

  if (CategoryHelper.isSphericalBalls(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, SPHERICAL_BALLS)
    );
  }

  if (CategoryHelper.isIgubalDoubleJoints(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, IGUBAL_DOUBLE_JOINTS_ORDER)
    );
  }

  if (CategoryHelper.isIgubalCouplingJoints(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, IGUBAL_COUPLING_JOINTS_ORDER)
    );
  }

  if (CategoryHelper.isDrygearAccessories(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, DRYGEAR_ACCESSORIES_ORDER)
    );
  }

  if (CategoryHelper.isDrygearWormGearJointsAsymmetrical(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, DRYGEAR_WORM_GEAR_JOINTS_ASYMMETRICAL_ORDER)
    );
  }

  if (CategoryHelper.isDrygearWormGearAccessories(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, DRYGEAR_WORM_GEAR_ACCESSORIES_ORDER)
    );
  }

  if (CategoryHelper.isMotorGearBox(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, MOTOR_GEAR_BOXES_ORDER)
    );
  }

  if (CategoryHelper.isBarStock(category)) {
    attributeDefinitionsSorted = attributeDefinitions.sort((a, b) =>
      sortByAnotherArray<string>(a.attribute_id, b.attribute_id, MOTOR_GEAR_BOXES_ORDER)
    );
  }

  const variantAxes: VariantAxis[] = [];

  let axisCount = 0;
  for (const attributeDefinition of attributeDefinitionsSorted) {
    const items: VariantAxisItem[] = generateAxisItems(
      axisCount,
      products,
      attributeDefinitions,
      attributeDefinition,
      selectedProduct
    );

    // create axis
    const variantAxis: VariantAxis = {
      attribute: attributeDefinition.attribute_id,
      label: attributeDefinition.label,
      readonly,
      items,
    };
    axisCount += 1;
    variantAxes.push(variantAxis);
  }

  return variantAxes;
};

/**
 * returns all information about the read only variant axes
 */
export const getReadOnlyVariantAxes = (
  products: Array<Pick<ProductFragmentEnhanced, 'attributes' | 'id'>>,
  attributes: string[],
  attributeDefinitions: AttributeDefinitionFragment[],
  category: string,
  locale: string
): VariantAxis[] => generateVariantAxes(products, attributes, attributeDefinitions, true, category, locale);

/**
 * returns all needed information about the selectable variant axes (for generating the generic variant selector)
 */
export const getSelectableVariantAxes = (
  products: Array<Pick<ProductFragmentEnhanced, 'attributes' | 'id'>>,
  attributes: string[],
  attributeDefinitions: AttributeDefinitionFragment[],
  category: string,
  locale: string,
  product: ProductFragmentEnhanced
): VariantAxis[] => generateVariantAxes(products, attributes, attributeDefinitions, false, category, locale, product);

/**
 * get the sku of that product of the given products that is most similar to productToMatch with one changed attribute.
 * Similarity is considered here as equality of attribute values.
 * Only those values of attributes defined in sortedAttributeDefinitions are respected.
 * The sort order of sortedAttributeDefinitions reflects the weighting of the attribute equality.
 * Equality of items with lower indexes in sortedAttributeDefinitions are weighted more then items with higher indexes.
 *
 * @param productToMatch
 * @param products
 * @param sortedAttributeDefinitions
 * @param valueOfChangedAttribute
 * @param keyOfChangedAttribute
 */

export const getSkuOfMostSimilarProduct = (
  productToMatch: ProductFragmentEnhanced,
  products: MinimalProductWithVariantAxesAttributes[],
  sortedAttributeDefinitions: AttributeDefinitionFragment[],
  valueOfChangedAttribute: string,
  keyOfChangedAttribute: string
): string => {
  let bestMatch: { rank: number; product: MinimalProductWithVariantAxesAttributes } = {
    rank: 0,
    product: {} as MinimalProductWithVariantAxesAttributes,
  };

  products.forEach(product => {
    // prepare attributes to search for in products
    const searchAttributes: { [key: string]: string | number } = {};
    sortedAttributeDefinitions.forEach(def => {
      const attributeValue = getAttributeValue<string | ProductAttributeValueAmountUnit>(
        productToMatch.attributes?.[def.attribute_id] as ProductAttribute<string | ProductAttributeValueAmountUnit>
      );
      searchAttributes[def.attribute_id] =
        (attributeValue as ProductAttributeValueAmountUnit).amount || (attributeValue as string);
    });
    searchAttributes[keyOfChangedAttribute] = valueOfChangedAttribute;

    // generate a ranking of the products by implementing a weighting binary->int algorithm
    let tempMatch = '1'; // prefix binary to be able to handle binary values starting with zeros

    for (const attrDef of sortedAttributeDefinitions) {
      const prodValue = getAttributeValue<string | ProductAttributeValueAmountUnit>(
        product.attributes?.[attrDef.attribute_id] as ProductAttribute<string | ProductAttributeValueAmountUnit>
      );
      const searchVal = searchAttributes[attrDef.attribute_id];
      if (typeof prodValue !== 'string') {
        tempMatch += prodValue.amount === parseFloat(String(searchVal)) ? 1 : 0;
      } else {
        tempMatch += prodValue === searchVal ? 1 : 0;
      }
    }

    if (parseInt(tempMatch, 2) > bestMatch.rank) {
      bestMatch = { rank: parseInt(tempMatch, 2), product };
    }
  });

  return bestMatch.product.id;
};

export const getMatchingProductsForSingleAttribute = (
  products: MinimalProductWithVariantAxesAttributes[],
  searchAttributeKey: string,
  searchAttributeValue: string
): MinimalProductWithVariantAxesAttributes[] =>
  products.filter((product): boolean => {
    const attributeValue = getAttributeValue<string | ProductAttributeValueAmountUnit>(
      product.attributes?.[searchAttributeKey] as ProductAttribute<string | ProductAttributeValueAmountUnit>
    );
    if (
      ((attributeValue as ProductAttributeValueAmountUnit).amount?.toString() || (attributeValue as string)) ===
      searchAttributeValue
    ) {
      return true;
    }
    return false;
  });

export const findNewVariantAxesByCategory = (
  primaryCategoryCode: string,
  newAvailableVariantAxes: VariantAxis[],
  allProducts: Array<Pick<ProductFragmentEnhanced, 'attributes' | 'id'>>,
  selectableVariantAxesAttributesDefinitions: AttributeDefinitionFragment[],
  newVariant?: ProductFragmentEnhanced
) => {
  if (primaryCategoryCode === PRIMARY_CATEGORY.TOOTHED_SLEWING_RINGS) {
    const foundVariantAxes = generateVariantAxes(
      allProducts,
      [],
      selectableVariantAxesAttributesDefinitions,
      false,
      PRIMARY_CATEGORY.TOOTHED_SLEWING_RINGS,
      '',
      newVariant
    );

    return foundVariantAxes;
  }
  if (primaryCategoryCode === PRIMARY_CATEGORY.SPHERICAL_BALLS) {
    const foundVariantAxes = generateVariantAxes(
      allProducts,
      [],
      selectableVariantAxesAttributesDefinitions,
      false,
      PRIMARY_CATEGORY.SPHERICAL_BALLS,
      '',
      newVariant
    );

    return foundVariantAxes;
  }

  return newAvailableVariantAxes;
};
